import React, {useState} from 'react';
import {theme} from '@styles/theme';
import Text from '@atoms/Text';
import {ICell, IModule} from '@types';
import {ChevronSort} from '@carbon/icons-react';
import {
  HeaderContent,
  ModuleCellContent,
  NotAvailable,
  SortIcon,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from './ModulesTable.styles';
import {
  CompareModuleName,
  HoverInfoWrapper,
  TableCell,
  TestCellContent,
} from '@organisms/CompareSubsystemTable.style';
import {CoverageChanges} from '@components/atoms/CoverageChanges';
import {HoverInfo} from '@organisms/CompareSubsystemTable.style';

interface ModulesTableProps {
  modules: IModule[];
  onSortTests: (testId: number) => void;
}

const CompareSubsystemTable: React.FC<ModulesTableProps> = ({modules, onSortTests}) => {
  const allTests = Array.from(
    new Set(modules.flatMap(module => module?.cells?.map(cell => cell.test_type_id))),
  );

  const testNames = modules.reduce<Record<string, string>>((acc, module) => {
    module.cells?.forEach(cell => {
      const key = String(cell.test_type_id);
      if (!acc[key]) {
        acc[key] = cell.test_type_name;
      }
    });
    return acc;
  }, {});

  const [hoveredCellKey, setHoveredCellKey] = useState<string | null>(null);

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Список модулей" type="bodyM" />
              </HeaderContent>
            </TableHeaderCell>

            {allTests.map(testId => (
              <TableHeaderCell key={`header-${testId}`}>
                <HeaderContent>
                  <Text text={testNames[Number(testId)] || ''} type="bodyM" />
                  <SortIcon onClick={() => onSortTests(Number(testId))}>
                    <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                  </SortIcon>
                </HeaderContent>
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {modules.map(module => (
            <TableRow key={module.id}>
              <TableCell
                onMouseEnter={() => setHoveredCellKey(`${module.id}-module`)}
                onMouseLeave={() => setHoveredCellKey(null)}>
                <ModuleCellContent>
                  <CompareModuleName>
                    <Text text={module.name} />
                    <CoverageChanges
                      coverage={module.auto_coverage + module.coverage}
                      changes={
                        (module.auto_coverage_delta ?? 0) + (module.coverage_delta ?? 0)
                      }
                    />
                  </CompareModuleName>
                </ModuleCellContent>
                {hoveredCellKey === `${module.id}-module` && (
                  <HoverInfo>
                    <HoverInfoWrapper>
                      <CoverageChanges
                        coverage={module.auto_coverage + module.coverage}
                        changes={
                          (module.auto_coverage_delta ?? 0) + (module.coverage_delta ?? 0)
                        }
                      />
                      <Text text="Общее покрытие тестом" type="tinyR" />
                    </HoverInfoWrapper>
                    <HoverInfoWrapper>
                      <CoverageChanges
                        coverage={module.auto_coverage}
                        changes={module.auto_coverage_delta ?? 0}
                        mark="auto"
                      />
                      <Text text="Автоматизированное" type="tinyR" />
                    </HoverInfoWrapper>
                    <HoverInfoWrapper>
                      <CoverageChanges
                        coverage={module.coverage}
                        changes={module.coverage_delta ?? 0}
                        mark="manual"
                      />
                      <Text text="Ручное тестирование" type="tinyR" />
                    </HoverInfoWrapper>
                  </HoverInfo>
                )}
              </TableCell>

              {allTests.map(testId => {
                const moduleTest: ICell | undefined = module.cells?.find(
                  t => t.test_type_id === testId,
                );

                return (
                  <TableCell
                    key={`${module.id}-${testId}`}
                    onMouseEnter={() => setHoveredCellKey(`${module.id}-${testId}`)}
                    onMouseLeave={() => setHoveredCellKey(null)}>
                    {moduleTest ? (
                      <TestCellContent>
                        <CoverageChanges
                          coverage={
                            (moduleTest.coverage ?? 0) + (moduleTest.auto_coverage ?? 0)
                          }
                          changes={
                            (moduleTest.auto_coverage_delta ?? 0) +
                            (moduleTest.coverage_delta ?? 0)
                          }
                          variant="stacked"
                        />
                      </TestCellContent>
                    ) : (
                      <TestCellContent>
                        <NotAvailable>
                          <Text
                            text={'\u00A0N/A'}
                            type="bodyNum"
                            color={theme.colors.basic03}
                          />
                        </NotAvailable>
                      </TestCellContent>
                    )}
                    {hoveredCellKey === `${module.id}-${testId}` && moduleTest && (
                      <HoverInfo>
                        <HoverInfoWrapper>
                          <CoverageChanges
                            coverage={
                              (moduleTest.coverage ?? 0) + (moduleTest.auto_coverage ?? 0)
                            }
                            changes={
                              (moduleTest.auto_coverage_delta ?? 0) +
                              (moduleTest.coverage_delta ?? 0)
                            }
                          />
                          <Text text="Общее покрытие тестом" type="tinyR" />
                        </HoverInfoWrapper>
                        <HoverInfoWrapper>
                          <CoverageChanges
                            coverage={moduleTest.auto_coverage ?? 0}
                            changes={moduleTest.auto_coverage_delta ?? 0}
                            mark="auto"
                          />
                          <Text text="Автоматизированное" type="tinyR" />
                        </HoverInfoWrapper>
                        <HoverInfoWrapper>
                          <CoverageChanges
                            coverage={moduleTest.coverage ?? 0}
                            changes={moduleTest.coverage_delta ?? 0}
                            mark="manual"
                          />
                          <Text text="Ручное тестирование" type="tinyR" />
                        </HoverInfoWrapper>
                      </HoverInfo>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CompareSubsystemTable;
